import {
  GidType,
  parseGid,
  toGid,
} from "@redotech/redo-model/shopify/gid-utils";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { ShopifyAjaxClient } from "@redotech/shopify-client/ajax";
import { StorefrontClient } from "../client/storefront";
import { log } from "../monitoring/log";
import { updateAndOpenCart } from "./add-product";

export interface ProductTrialInfo {
  productGid: string;
  variantGid: string;
  sellingPlanGid: string;
  sellingPlanGroupGid: string;
  trialLengthDays: number;
}

export interface AddToCartWithTrialParams {
  productGid: string;
  variantGid: string;
  attributes?: Record<string, string>;
}

export interface GetProductTrialInfoParams {
  productGid: string;
  variantGid?: string;
}

export function createTryFirstAPI({
  autocheck,
  extensionShopifyClient,
}: {
  autocheck: WidgetAutocheck;
  extensionShopifyClient: StorefrontClient;
}) {
  if (!globalThis.redoStorefront) {
    console.log(
      "Could not initialize Try First API because redoStorefront is not defined",
    );
    return;
  }
  async function addToCartWithTrial(
    params: AddToCartWithTrialParams,
  ): Promise<boolean> {
    try {
      log("Try First API: addToCartWithTrial called", params);

      const { productGid, variantGid, attributes = {} } = params;

      // Validate GIDs
      if (!productGid || !variantGid) {
        log("Try First API: Missing required productGid or variantGid");
        return false;
      }

      const variantId = parseGid(variantGid);
      const tryFirstSellingPlanId = autocheck.tryFirst?.sellingPlanId;

      if (!tryFirstSellingPlanId) {
        log("Try First API: No Try First selling plan configured");
        return false;
      }

      // Convert attributes to properties format expected by Shopify
      const properties: Record<string, string> = {};
      for (const [key, value] of Object.entries(attributes)) {
        properties[key] = value;
      }

      const ajaxClient = ShopifyAjaxClient.fromEnv();
      await ajaxClient.cartAdd({
        items: [
          {
            id: parseInt(variantId, 10),
            quantity: 1,
            selling_plan: parseInt(tryFirstSellingPlanId, 10),
            properties,
          },
        ],
      });

      await updateAndOpenCart();

      log("Try First API: Successfully added product to cart with trial");
      return true;
    } catch (error) {
      log("Try First API: Error adding product to cart", { error });
      return false;
    }
  }

  async function getProductTrialInfo(
    params: GetProductTrialInfoParams,
  ): Promise<ProductTrialInfo[]> {
    try {
      log("Try First API: getProductTrialInfo called", params);

      const { productGid, variantGid } = params;

      if (!productGid) {
        log("Try First API: Missing required productGid");
        return [];
      }

      const sellingPlans =
        await extensionShopifyClient.getProductSellingPlansByGid(productGid);

      if (!sellingPlans || sellingPlans.length === 0) {
        log("Try First API: No selling plans found for product", {
          productGid,
        });
        return [];
      }

      const tryFirstSellingPlanId = autocheck.tryFirst?.sellingPlanId;
      if (!tryFirstSellingPlanId) {
        log("Try First API: No Try First selling plan configured");
        return [];
      }

      const matchingPlan = sellingPlans.find((plan) => {
        const planId = parseGid(plan.id);
        return planId === tryFirstSellingPlanId;
      });

      if (!matchingPlan) {
        return [];
      }

      const trialLengthDays =
        autocheck.tryFirst?.template?.trialPeriodDays ?? 7;

      const trialInfo: ProductTrialInfo = {
        productGid,
        variantGid: toGid(GidType.PRODUCT_VARIANT, variantGid ?? 0),
        sellingPlanGid: toGid(GidType.SELLING_PLAN, tryFirstSellingPlanId),
        sellingPlanGroupGid: toGid(
          GidType.SELLING_PLAN_GROUP,
          autocheck.tryFirst?.sellingPlanGroupId ?? 0,
        ),
        trialLengthDays,
      };

      log("Try First API: Successfully retrieved trial info", trialInfo);
      return [trialInfo];
    } catch (error) {
      log("Try First API: Error getting product trial info", { error });
      return [];
    }
  }

  globalThis.redoStorefront.tryFirst = {
    addToCartWithTrial,
    getProductTrialInfo,
  };
}
